import { Middleware } from '@reduxjs/toolkit';
import { getMapPrice } from './image';
import { RootState } from './index';
import { PriceRequest } from '../types/api';
import { getPins, PIN_WIDTH_PX } from '../components/mapmaker/pins';
import { BorderInfo } from '../components/mapmaker/constants';

// Actions that should trigger a price update
const PRICE_TRIGGERING_ACTIONS = [
  'map/setStyle',
  'map/setLayout', 
  'map/setSize',
  'map/setBorder',
  'map/setBorderState',
  'map/updatePinCount',
  'map/triggerPriceUpdate',
  'image/setCurrency',
];

// Debounce timer to prevent excessive API calls
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_DELAY = 500; // 500ms debounce

/**
 * Middleware that automatically fetches price when relevant map state changes.
 * This ensures price updates are always triggered without relying on React hooks.
 */
export const priceMiddleware: Middleware<object, RootState> = (store) => (next) => (action) => {
  // Let the action pass through first
  const result = next(action);
  
  // Check if this action should trigger a price update
  if (PRICE_TRIGGERING_ACTIONS.includes(action.type)) {
    // Clear any existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Debounce the price fetch
    debounceTimer = setTimeout(() => {
      const state = store.getState();
      const { map: mapState, image: imageState } = state;
      
      // Only fetch if map is ready
      if (!mapState.map || !mapState.map.isStyleLoaded()) {
        return;
      }
      
      try {
        const map = mapState.map;
        
        // Get pins from the map
        const pins = getPins(map);
        
        // Get map bounds
        const topLeft = map.unproject([0, 0]);
        const { width: canvasWidth, height: canvasHeight } = map.getCanvas().getBoundingClientRect();
        const bottomRight = map.unproject([canvasWidth, canvasHeight]);
        
        // Filter pins that are within the visible area
        const validPins = pins.filter((pin) =>
          pin.lat > bottomRight.lat &&
          pin.lng < bottomRight.lng &&
          pin.lat < topLeft.lat &&
          pin.lng > topLeft.lng
        );

        // Build the price request
        const priceRequest: PriceRequest = {
          pins: validPins,
          pinScale: PIN_WIDTH_PX / canvasWidth,
          minLat: bottomRight.lat,
          minLng: topLeft.lng,
          maxLat: topLeft.lat,
          size: mapState.size,
          orientation: mapState.layout,
          style: mapState.style,
          borderFilter: BorderInfo[mapState.border]?.id,
          borderState: mapState.borderState,
          currency: imageState.currency,
        };

        // Dispatch the price fetch action
        store.dispatch(getMapPrice(priceRequest));
      } catch (error) {
        console.error('Error fetching price in middleware:', error);
      }
    }, DEBOUNCE_DELAY);
  }
  
  return result;
};
